import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { FiChevronDown, FiChevronUp, FiMail, FiPlus, FiSearch, FiSettings, FiUserPlus, FiX } from "react-icons/fi";
import { usePermissions } from "../Auth/PermissionsContext";
import { expandGrants } from "../Auth/permissionTokens";
import GatedButton from "../Components/GatedButton";
import { UserAvatar } from "../Components/UserAvatar";
import { RoleBadgeChip } from "../Components/RoleBadges";
import { AdminUser, AssignableRole, usersClient } from "./Users/usersClient";
import { identityProviderLabel, isPendingExternal } from "./Users/userScreening";
import { groupByPerson, impactOfRoles, matchesUserFilter, USER_FILTERS, type UserFilterKey, type UserPerson } from "./Users/userGrouping";
import { ManageUserModal } from "./Users/ManageUserModal";
import { TicketDefaultsCard } from "../Components/Tickets/TicketDefaultsCard";
import { NotificationRecipientsCard } from "../Components/Users/NotificationRecipientsCard";

/** Admin user management : create/edit/delete accounts and assign RBAC roles.
 * Backed by Auth0 in production or the local DB in dev. Creating an account never sets a password:
 * Auth0 emails the new user a link to choose their own (they can also reset it any time). Only
 * accounts created here (the allowlist) may sign in : via email/password or Google on that email. */
type SortField = "name" | "lastLogin" | "status";

/** A column header that toggles sorting on click, with an active-direction caret. */
function SortButton({ label, field, active, dir, onClick }: {
	label: string;
	field: SortField;
	active: SortField;
	dir: "asc" | "desc";
	onClick: (f: SortField) => void;
}): React.ReactElement {
	const isActive = active === field;
	return (
		<button
			type="button"
			onClick={() => onClick(field)}
			className={`group inline-flex items-center gap-1 font-semibold uppercase tracking-wide transition ${isActive ? "text-slate-700" : "text-slate-500 hover:text-slate-700"}`}
			title={`Trier par ${label.toLowerCase()}`}
		>
			{label}
			{isActive ? (
				dir === "asc" ? <FiChevronUp className="h-3.5 w-3.5" /> : <FiChevronDown className="h-3.5 w-3.5" />
			) : (
				<FiChevronUp className="h-3.5 w-3.5 opacity-0 transition group-hover:opacity-40" />
			)}
		</button>
	);
}

export default function Users(): React.ReactElement {
	const { can } = usePermissions();
	const canWrite = can("feature:users:write");

	const [users, setUsers] = useState<AdminUser[]>([]);
	const [roles, setRoles] = useState<AssignableRole[]>([]);
	const [source, setSource] = useState<string>("local");
	const [loading, setLoading] = useState(true);
	const [search, setSearch] = useState("");
	const [filter, setFilter] = useState<UserFilterKey>("all");
	const [managing, setManaging] = useState<string | null>(null); // clé de la personne ouverte dans « Gérer »
	const [creating, setCreating] = useState(false);
	const [sortField, setSortField] = useState<"name" | "lastLogin" | "status">("name");
	const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

	const toggleSort = (field: "name" | "lastLogin" | "status") => {
		if (field === sortField) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
		else {
			setSortField(field);
			setSortDir(field === "lastLogin" ? "desc" : "asc");
		}
	};

	// Les rôles assignables ne dépendent pas de la recherche : une seule requête au montage.
	useEffect(() => {
		usersClient
			.assignableRoles()
			.then(setRoles)
			.catch(() => {
				/* le tableau reste lisible sans les libellés de rôles */
			});
	}, []);

	// La saisie ne part PAS en requête à chaque frappe : Auth0 limite le débit du Management
	// API et chaque échec levait son propre toast. On attend une pause de saisie, on ignore
	// les réponses arrivées après une requête plus récente, et l'erreur ne s'affiche qu'une
	// fois (toastId) sans vider la liste déjà à l'écran.
	const requestSeq = useRef(0);
	const load = useCallback(async () => {
		const seq = ++requestSeq.current;
		setLoading(true);
		try {
			const list = await usersClient.list(search.trim() || undefined);
			if (seq !== requestSeq.current) return;
			setUsers(list.users);
			setSource(list.source);
		} catch (e: any) {
			if (seq !== requestSeq.current) return;
			toast.error(e?.response?.data?.message ?? "Impossible de charger les utilisateurs.", { toastId: "users-load" });
		} finally {
			if (seq === requestSeq.current) setLoading(false);
		}
	}, [search]);

	useEffect(() => {
		const handle = window.setTimeout(() => {
			void load();
		}, search ? 400 : 0);
		return () => window.clearTimeout(handle);
	}, [load, search]);

	const roleName = useMemo(() => {
		const m = new Map(roles.map((r) => [r.key, r.name]));
		return (key: string) => m.get(key) ?? key;
	}, [roles]);
	// Pastille du rôle (mêmes couleurs/couronne que la barre du haut) ; ICI sans dominance :
	// l'écran d'administration montre la liste COMPLÈTE des rôles portés.
	const roleBadge = useMemo(() => {
		const m = new Map(roles.map((r) => [r.key, r.badge ?? null]));
		return (key: string) => m.get(key) ?? null;
	}, [roles]);

	// Les comptes externes « en attente » (sans rôle, e-mail hors @bcc.cd) sortent du tableau
	// principal : ils vivent dans la liste repliée dédiée ci-dessous, qu'il faut déplier
	// volontairement.
	const activeUsers = useMemo(() => users.filter((u) => !isPendingExternal(u)), [users]);
	const pendingUsers = useMemo(() => users.filter(isPendingExternal), [users]);
	const [pendingOpen, setPendingOpen] = useState(false);

	// UNE ligne par PERSONNE : l'annuaire garde un enregistrement par identité (e-mail / mot de
	// passe, Google…) et la même adresse apparaissait plusieurs fois. Les identités sont fusionnées
	// par e-mail ; la fiche « Gérer » agit sur toutes.
	const persons = useMemo(() => groupByPerson(activeUsers), [activeUsers]);
	const pendingPersons = useMemo(() => groupByPerson(pendingUsers), [pendingUsers]);
	const rolesByKey = useMemo(() => new Map(roles.map((r) => [r.key, r])), [roles]);
	const { catalog } = usePermissions();
	const impactOf = useCallback((p: UserPerson) => impactOfRoles(p.roles, rolesByKey, catalog), [rolesByKey, catalog]);

	// Compteurs des filtres (sur les personnes actives, avant filtre).
	const filterCounts = useMemo(() => {
		const counts: Record<UserFilterKey, number> = { all: 0, admins: 0, production: 0, contributors: 0, blocked: 0 };
		for (const p of persons) {
			const impact = impactOf(p);
			for (const f of USER_FILTERS) if (matchesUserFilter(f.key, p, impact)) counts[f.key]++;
		}
		return counts;
	}, [persons, impactOf]);

	const filteredPersons = useMemo(() => persons.filter((p) => matchesUserFilter(filter, p, impactOf(p))), [persons, filter, impactOf]);

	// Client-side sort of the already-filtered list. "name" sorts on the shown label (name, else email);
	// "lastLogin" nulls sink to the bottom; "status" groups actifs vs bloqués.
	const sortedPersons = useMemo(() => {
		const dir = sortDir === "asc" ? 1 : -1;
		return [...filteredPersons].sort((a, b) => {
			let r = 0;
			if (sortField === "name") {
				r = (a.name || a.email || "").localeCompare(b.name || b.email || "", "fr", { sensitivity: "base" });
			} else if (sortField === "lastLogin") {
				const ta = a.lastLogin ? new Date(a.lastLogin).getTime() : 0;
				const tb = b.lastLogin ? new Date(b.lastLogin).getTime() : 0;
				r = ta - tb;
			} else {
				r = (a.anyBlocked ? 1 : 0) - (b.anyBlocked ? 1 : 0);
			}
			return r * dir;
		});
	}, [filteredPersons, sortField, sortDir]);

	const managed = useMemo(() => (managing ? [...persons, ...pendingPersons].find((p) => p.key === managing) ?? null : null), [managing, persons, pendingPersons]);

	// Perimetre affiche par la recherche + le filtre d'impact : les matrices repliees plus bas
	// (assignes de tickets, destinataires de notifications) s'y limitent, pour qu'un filtre
	// « Admins » ou une recherche par nom vaille pour TOUTE la page, pas seulement le tableau.
	const visibleEmails = useMemo(
		() => new Set(filteredPersons.map((p) => (p.email || "").toLowerCase()).filter(Boolean)),
		[filteredPersons]
	);

	return (
		<div className="mx-auto w-full max-w-6xl px-6 py-8">
			<div className="flex flex-wrap items-end justify-between gap-4">
				<div>
					<h1 className="text-2xl font-bold text-slate-900">Utilisateurs</h1>
					<p className="mt-1 text-sm text-slate-500">
						Gérez les comptes administrateurs et leurs rôles.{" "}
						<span className="font-medium text-slate-600">Source : {source === "auth0" ? "Auth0" : "Base locale"}</span>
					</p>
				</div>
				<GatedButton
					token="feature:users:write"
					onClick={() => setCreating(true)}
					className="inline-flex items-center gap-2 rounded-xl bg-[#0095DB] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#007cb8]"
				>
					<FiUserPlus className="h-4 w-4" /> Nouvel utilisateur
				</GatedButton>
			</div>

			<div className="mt-6 flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 shadow-sm">
				<FiSearch className="h-4 w-4 shrink-0 text-slate-400" />
				<input
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					placeholder="Rechercher par e-mail ou nom…"
					className="min-w-[200px] flex-1 bg-transparent text-sm text-slate-700 outline-none placeholder:text-slate-400"
				/>
				{/* Filtres d'impact : qui administre, qui peut toucher la production, qui contribue seulement. */}
				<div className="flex flex-wrap items-center gap-1" role="group" aria-label="Filtrer les comptes">
					{USER_FILTERS.map((f) => {
						const active = filter === f.key;
						const count = filterCounts[f.key];
						return (
							<button
								key={f.key}
								type="button"
								onClick={() => setFilter(f.key)}
								aria-pressed={active}
								title={f.hint}
								className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold transition ${
									active ? "border-[#0F2849] bg-[#0F2849] text-white" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
								} ${f.key === "production" && !active ? "border-emerald-200 text-emerald-700" : ""}`}
							>
								{f.label}
								<span className={`rounded-full px-1.5 text-[10px] tabular-nums ${active ? "bg-white/20" : "bg-slate-100 text-slate-500"}`}>{count}</span>
							</button>
						);
					})}
				</div>
			</div>

			<div className="mt-4 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
				<table className="w-full text-left text-sm">
					<thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
						<tr>
							<th className="px-5 py-3 font-semibold">
								<SortButton label="Utilisateur" field="name" active={sortField} dir={sortDir} onClick={toggleSort} />
							</th>
							<th className="px-5 py-3 font-semibold">Rôles</th>
							<th className="px-5 py-3 font-semibold" title="Moyens de connexion de la personne (fusionnés par e-mail)">Identités</th>
							<th className="px-5 py-3 font-semibold">
								<SortButton label="Dernière connexion" field="lastLogin" active={sortField} dir={sortDir} onClick={toggleSort} />
							</th>
							{source === "auth0" && (
								<th className="px-5 py-3 font-semibold" title="Dernier changement de mot de passe : libre-service, e-mail de réinitialisation ou forçage administrateur">
									Mot de passe modifié
								</th>
							)}
							<th className="px-5 py-3 font-semibold">
								<SortButton label="Statut" field="status" active={sortField} dir={sortDir} onClick={toggleSort} />
							</th>
							<th className="px-5 py-3 text-right font-semibold">Actions</th>
						</tr>
					</thead>
					<tbody className="divide-y divide-slate-100">
						{loading ? (
							<tr>
								<td colSpan={source === "auth0" ? 7 : 6} className="px-5 py-10 text-center text-slate-400">
									Chargement…
								</td>
							</tr>
						) : sortedPersons.length === 0 ? (
							<tr>
								<td colSpan={source === "auth0" ? 7 : 6} className="px-5 py-10 text-center text-slate-400">
									{persons.length === 0 ? "Aucun utilisateur." : "Aucun compte ne correspond à ce filtre."}
								</td>
							</tr>
						) : (
							sortedPersons.map((p) => {
								const primary = p.identities[0];
								const impact = impactOf(p);
								return (
									<tr key={p.key} className="hover:bg-slate-50/60">
										<td className="px-5 py-3">
											<Link to={`/account/${primary.id}`} className="flex items-center gap-3 rounded-lg -m-1 p-1 transition hover:bg-slate-100" title={`Voir le profil de ${p.name}`}>
												<UserAvatar name={p.name} id={primary.id} avatarKey={p.avatarKey} pictureUrl={p.picture} sizeClass="h-9 w-9" textClass="text-xs" />
												<div className="min-w-0">
													<p className="flex items-center gap-1.5 truncate font-medium text-slate-800">
														{p.name}
														{impact.admin ? (
															<span className="rounded bg-[#0F2849] px-1.5 py-px text-[10px] font-semibold text-white" title="Administration complète">Admin</span>
														) : impact.production ? (
															<span className="rounded bg-emerald-50 px-1.5 py-px text-[10px] font-semibold text-emerald-700" title="Peut mettre du contenu en production">Production</span>
														) : null}
													</p>
													<p className="truncate text-xs text-slate-400">{p.email}</p>
												</div>
											</Link>
										</td>
										<td className="px-5 py-3">
											<div className="flex flex-wrap gap-1.5">
												{p.roles.length === 0 ? (
													<span className="text-xs text-slate-400">-</span>
												) : (
													p.roles.map((r) => {
														const badge = roleBadge(r);
														return badge ? (
															<RoleBadgeChip key={r} badge={badge} className="px-2.5 text-xs" />
														) : (
															<span key={r} className="rounded-full bg-sky-50 px-2.5 py-0.5 text-xs font-medium text-sky-700">
																{roleName(r)}
															</span>
														);
													})
												)}
											</div>
										</td>
										<td className="px-5 py-3">
											<div className="flex flex-wrap gap-1">
												{p.identities.map((u) => (
													<span
														key={u.id}
														className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${u.blocked ? "bg-rose-50 text-rose-600 line-through" : identityProviderLabel(u.id) === "E-mail / mot de passe" ? "bg-slate-100 text-slate-600" : "bg-sky-50 text-sky-700"}`}
														title={`${identityProviderLabel(u.id)}${u.blocked ? " (bloquée)" : ""} · ${u.id}`}
													>
														{identityProviderLabel(u.id)}
													</span>
												))}
											</div>
										</td>
										<td className="px-5 py-3 text-xs text-slate-500">
											{p.lastLogin ? new Date(p.lastLogin).toLocaleString("fr-FR") : <span className="text-slate-400">-</span>}
										</td>
										{source === "auth0" && (
											<td className="px-5 py-3 text-xs text-slate-500">
												{!p.hasPassword ? (
													<span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600" title="Connexion sociale uniquement : l'authentification est déléguée au fournisseur externe, sans mot de passe applicatif">
														Connexion sociale
													</span>
												) : p.lastPasswordReset ? (
													<span title="Dernier changement : libre-service, e-mail de réinitialisation ou forçage administrateur">{new Date(p.lastPasswordReset).toLocaleString("fr-FR")}</span>
												) : (
													<span className="inline-flex items-center rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-700" title="Aucun changement de mot de passe enregistré depuis la création du compte">
														Jamais changé
													</span>
												)}
											</td>
										)}
										<td className="px-5 py-3">
											{p.blocked ? (
												<span className="rounded-full bg-rose-50 px-2.5 py-0.5 text-xs font-semibold text-rose-600">Bloqué</span>
											) : p.anyBlocked ? (
												<span className="rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-700" title="Au moins une identité est bloquée">Partiel</span>
											) : (
												<span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-600">Actif</span>
											)}
										</td>
										<td className="px-5 py-3">
											<div className="flex justify-end">
												<button
													type="button"
													onClick={() => setManaging(p.key)}
													className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
													title="Ouvrir la fiche : rôles, sécurité, identités, activité"
												>
													<FiSettings className="h-3.5 w-3.5" aria-hidden /> Gérer
												</button>
											</div>
										</td>
									</tr>
								);
							})
						)}
					</tbody>
				</table>
			</div>

			{/* Comptes arrivés par une connexion sociale, sans rôle : repliés par défaut : il faut
			    cliquer pour les voir. Tant qu'ils sont ici, l'annuaire des pickers (assignés de
			    tickets, défauts, destinataires de notifications) ne les propose pas ; attribuer un
			    rôle via « Modifier » vaut approbation et les fait remonter dans le tableau. */}
			{!loading && pendingUsers.length > 0 && (
				<div className="mt-4 overflow-hidden rounded-2xl border border-amber-200 bg-amber-50/60 shadow-sm">
					<button
						type="button"
						onClick={() => setPendingOpen((v) => !v)}
						aria-expanded={pendingOpen}
						className="flex w-full items-center justify-between px-5 py-3.5 text-left transition hover:bg-amber-50"
					>
						<span className="flex items-center gap-2 text-sm font-semibold text-amber-800">
							En attente d'approbation
							<span className="rounded-full bg-amber-200 px-2 py-0.5 text-xs font-bold text-amber-900">{pendingUsers.length}</span>
						</span>
						{pendingOpen ? <FiChevronUp className="h-4 w-4 text-amber-700" /> : <FiChevronDown className="h-4 w-4 text-amber-700" />}
					</button>
					{pendingOpen && (
						<div className="border-t border-amber-200 bg-white">
							<p className="px-5 pt-3 text-xs text-slate-500">
								Comptes connectés via un fournisseur social, sans rôle attribué. Ils n'apparaissent ni dans les
								assignations de tickets ni dans les destinataires de notifications. Attribuer un rôle (« Gérer »)
								vaut approbation ; la suppression se fait dans la même fiche.
							</p>
							<ul className="divide-y divide-slate-100 px-2 py-2">
								{pendingPersons.map((p) => {
									const u = p.identities[0];
									return (
										<li key={p.key} className="flex items-center justify-between gap-3 rounded-lg px-3 py-2.5 hover:bg-slate-50">
											<div className="flex min-w-0 items-center gap-3">
												<UserAvatar name={p.name} id={u.id} avatarKey={p.avatarKey} pictureUrl={p.picture} sizeClass="h-9 w-9" textClass="text-xs" />
												<div className="min-w-0">
													<p className="truncate text-sm font-medium text-slate-800">{p.name}</p>
													<p className="truncate text-xs text-slate-400">{p.email}</p>
												</div>
												{p.identities.map((i) => (
													<span key={i.id} className="ml-2 shrink-0 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600">
														{identityProviderLabel(i.id)}
													</span>
												))}
												{p.lastLogin && (
													<span className="hidden shrink-0 text-xs text-slate-400 sm:inline">
														Vu le {new Date(p.lastLogin).toLocaleDateString("fr-FR")}
													</span>
												)}
											</div>
											<div className="flex shrink-0 justify-end gap-1">
												<button
													type="button"
													onClick={() => setManaging(p.key)}
													className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
													title="Gérer (attribuer un rôle = approuver, ou supprimer)"
												>
													<FiSettings className="h-3.5 w-3.5" aria-hidden /> Gérer
												</button>
											</div>
										</li>
									);
								})}
							</ul>
						</div>
					)}
				</div>
			)}

			{creating && canWrite && (
				<UserModal
					user={null}
					roles={roles}
					isAuth0={source === "auth0"}
					onClose={() => setCreating(false)}
					onSaved={() => {
						setCreating(false);
						void load();
					}}
				/>
			)}

			{managed && (
				<ManageUserModal
					person={managed}
					roles={roles}
					isAuth0={source === "auth0"}
					onClose={() => setManaging(null)}
					onChanged={() => void load()}
				/>
			)}

			{can("feature:tickets:write") && (
				<div className="mt-6">
					<TicketDefaultsCard visibleEmails={visibleEmails} />
				</div>
			)}

			{can("feature:users:write") && (
				<div className="mt-6">
					<NotificationRecipientsCard visibleEmails={visibleEmails} roles={roles} />
				</div>
			)}
		</div>
	);
}

function UserModal({
	user,
	roles,
	isAuth0,
	onClose,
	onSaved
}: {
	user: AdminUser | null;
	roles: AssignableRole[];
	isAuth0: boolean;
	onClose: () => void;
	onSaved: () => void;
}): React.ReactElement {
	const isEdit = !!user;
	const { permissions: callerTokens, catalog } = usePermissions();
	const [email, setEmail] = useState(user?.email ?? "");
	const [name, setName] = useState(user?.name ?? "");
	const [blocked, setBlocked] = useState(user?.blocked ?? false);
	const [selected, setSelected] = useState<Set<string>>(new Set(user?.roles ?? []));
	const [saving, setSaving] = useState(false);

	// Règle de sous-ensemble à l'attribution (miroir du serveur, qui reste l'autorité : 403 sinon) :
	// un rôle n'est activement proposable que si son set EXPANSÉ est inclus dans celui de l'appelant.
	// Les jetons en excès alimentent le tooltip du rôle désactivé. Les rôles DÉJÀ détenus par
	// l'utilisateur édité restent toujours décochables (retirer un rôle est libre).
	const excessByRole = useMemo(() => {
		const m = new Map<string, string[]>();
		if (!catalog) return m; // catalogue pas encore chargé → on laisse le serveur trancher
		const mine = callerTokens ?? new Set<string>();
		for (const r of roles) {
			const excess = Array.from(expandGrants(catalog, r.permissions ?? []))
				.filter((t) => !mine.has(t))
				.sort();
			m.set(r.key, excess);
		}
		return m;
	}, [roles, catalog, callerTokens]);

	const originallyHeld = useMemo(
		() => new Set((user?.roles ?? []).map((r) => r.trim().toLowerCase())),
		[user]
	);

	const toggleRole = (key: string) =>
		setSelected((prev) => {
			const next = new Set(prev);
			if (next.has(key)) next.delete(key);
			else next.add(key);
			return next;
		});

	const save = async () => {
		setSaving(true);
		try {
			if (isEdit) {
				await usersClient.update(user!.id, { name: name ?? "", blocked, roles: Array.from(selected) });
				toast.success("Utilisateur mis à jour.");
			} else {
				await usersClient.create({
					email,
					name: name || undefined,
					roles: Array.from(selected)
				});
				toast.success(isAuth0 ? "Utilisateur créé. Un e-mail de définition du mot de passe a été envoyé." : "Utilisateur créé.");
			}
			onSaved();
		} catch (e: any) {
			toast.error(e?.response?.data?.message ?? "Enregistrement impossible.");
		} finally {
			setSaving(false);
		}
	};

	return (
		<div className="fixed inset-0 z-[2000] flex items-center justify-center bg-slate-900/40 p-4">
			<div className="w-full max-w-lg rounded-2xl bg-white shadow-xl">
				<div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
					<h2 className="text-lg font-semibold text-slate-900">{isEdit ? "Modifier l'utilisateur" : "Nouvel utilisateur"}</h2>
					<button type="button" onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100">
						<FiX className="h-5 w-5" />
					</button>
				</div>
				<div className="max-h-[70vh] space-y-4 overflow-auto px-6 py-5">
					<Field label="Adresse e-mail">
						<input
							type="email"
							value={email}
							disabled={isEdit}
							onChange={(e) => setEmail(e.target.value)}
							className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm disabled:bg-slate-50 disabled:text-slate-400"
						/>
					</Field>
					<Field label="Nom">
						<input value={name ?? ""} onChange={(e) => setName(e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
					</Field>
					{!isEdit && isAuth0 && (
						<div className="flex items-start gap-2 rounded-lg border border-sky-100 bg-sky-50 px-3 py-2.5 text-xs text-sky-800">
							<FiMail className="mt-0.5 h-4 w-4 shrink-0" />
							<span>
								Aucun mot de passe à saisir : un e-mail sera automatiquement envoyé à l'utilisateur pour qu'il
								définisse le sien. Il pourra ensuite se connecter par e-mail/mot de passe ou avec Google.
							</span>
						</div>
					)}
					{isEdit && (
						<label className="flex items-center gap-2 text-sm text-slate-700">
							<input type="checkbox" checked={blocked} onChange={(e) => setBlocked(e.target.checked)} /> Bloquer la connexion de cet utilisateur
						</label>
					)}
					<div>
						<p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Rôles</p>
						<div className="grid gap-2 sm:grid-cols-2">
							{roles.map((r) => {
								const excess = excessByRole.get(r.key) ?? [];
								// Un rôle déjà détenu reste toujours manipulable : le retirer est libre.
								const blocked = !originallyHeld.has(r.key.trim().toLowerCase()) && excess.length > 0;
								const tooltip = blocked
									? `Accorde des permissions que vous ne détenez pas : ${excess.slice(0, 3).join(", ")}${
											excess.length > 3 ? ` +${excess.length - 3}` : ""
										}`
									: undefined;
								return (
									<label
										key={r.key}
										title={tooltip}
										className={`flex items-start gap-2 rounded-xl border p-3 text-sm transition ${
											blocked
												? "cursor-not-allowed border-slate-200 bg-slate-50 opacity-60"
												: selected.has(r.key)
													? "cursor-pointer border-sky-300 bg-sky-50"
													: "cursor-pointer border-slate-200 hover:bg-slate-50"
										}`}
									>
										<input
											type="checkbox"
											className="mt-0.5"
											disabled={blocked}
											checked={selected.has(r.key)}
											onChange={() => toggleRole(r.key)}
										/>
										<span>
											<span className="font-medium text-slate-800">{r.name}</span>
											{r.description && <span className="block text-xs text-slate-400">{r.description}</span>}
											{blocked && (
												<span className="block text-[11px] font-medium text-slate-400">
													Rôle non attribuable avec vos permissions
												</span>
											)}
										</span>
									</label>
								);
							})}
						</div>
					</div>
				</div>
				<div className="flex justify-end gap-2 border-t border-slate-100 px-6 py-4">
					<button type="button" onClick={onClose} className="rounded-xl px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100">
						Annuler
					</button>
					<button
						type="button"
						onClick={save}
						disabled={saving || (!isEdit && !email)}
						className="inline-flex items-center gap-2 rounded-xl bg-[#0095DB] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[#007cb8] disabled:opacity-50"
					>
						<FiPlus className="h-4 w-4" /> {isEdit ? "Enregistrer" : "Créer"}
					</button>
				</div>
			</div>
		</div>
	);
}

function Field({ label, children }: { label: string; children: React.ReactNode }): React.ReactElement {
	return (
		<label className="block">
			<span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</span>
			{children}
		</label>
	);
}
